'use client'

import { useTranslations } from 'next-intl'

import type { TaskListTypeFilter } from '@/features/tasks/api/watchTaskApi'
import { getTaskCardCtaLabelKey } from '@/features/tasks/taskCardCta'

import {
  TASK_FEED_PLATFORM_ICON,
  TASK_FEED_PLATFORM_LABEL,
  TASK_FEED_TYPE_ICON,
  type TaskFeedPlatform
} from '../TaskFeedCard/taskFeedCard.constants'
import type { FeedMultiFilter } from './FeedMenuLists'
import { FEED_PLATFORM_GROUPS } from './tasksFeedNav.constants'

export const PLATFORMS_FILTER_ID = 'platforms'
export const TYPES_FILTER_ID = 'types'

/** The Platforms menu over the platforms the tab's lists offer. */
export function usePlatformsFilter(
  offered: readonly TaskFeedPlatform[],
  picked: readonly TaskFeedPlatform[],
  onChange: (next: TaskFeedPlatform[]) => void
): FeedMultiFilter {
  const t = useTranslations('tasksPage.feedNav')
  return {
    id: PLATFORMS_FILTER_ID,
    label: t('platforms'),
    allLabel: t('allPlatforms'),
    ariaLabel: t('platformsAriaLabel'),
    groups: FEED_PLATFORM_GROUPS.map(group => ({
      label: t(group.labelKey),
      options: group.platforms
        .filter(platform => offered.includes(platform))
        .map(platform => {
          const Icon = TASK_FEED_PLATFORM_ICON[platform]
          return {
            value: platform,
            label: TASK_FEED_PLATFORM_LABEL[platform],
            icon: <Icon />
          }
        })
    })).filter(group => group.options.length > 0),
    value: picked,
    onChange: next => {
      onChange(next as TaskFeedPlatform[])
    }
  }
}

/** The Task type menu of My tasks. */
export function useTypesFilter(
  types: readonly TaskListTypeFilter[],
  picked: readonly TaskListTypeFilter[],
  onChange: (next: TaskListTypeFilter[]) => void
): FeedMultiFilter {
  const t = useTranslations('tasksPage.feedNav')
  const tCard = useTranslations('tasksPage.taskCard')
  return {
    id: TYPES_FILTER_ID,
    label: t('taskType'),
    allLabel: t('allTypes'),
    ariaLabel: t('taskTypeAriaLabel'),
    groups: [
      {
        options: types.map(type => {
          const Icon = TASK_FEED_TYPE_ICON[type]
          return {
            value: type,
            label: tCard(getTaskCardCtaLabelKey(type)),
            icon: <Icon />
          }
        })
      }
    ],
    value: picked,
    onChange: next => {
      onChange(next as TaskListTypeFilter[])
    }
  }
}
